import { useEffect, useState } from "react";
import { Ban, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

// Decisione salvata nel browser: "1" = ha dichiarato 18+, "0" = ha dichiarato di
// essere minorenne. localStorage e non sessionStorage perche' un gate che
// ricompare a ogni tab e' solo attrito per il maggiorenne, mentre per il
// minorenne deve sopravvivere alla chiusura del browser.
// "0" non e' piu' scrivibile dalla UI (il gate ha la sola conferma 18+), ma va
// ancora letto: chi si era dichiarato minorenne quando il bottone esisteva
// resterebbe altrimenti riammesso al primo reload.
const LS_KEY = "age_gate_ok";

type Decision = "pending" | "adult" | "minor";

function readDecision(): Decision {
  if (typeof window === "undefined") return "adult"; // SSR/prerender: mai bloccare
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === "1") return "adult";
    if (v === "0") return "minor";
  } catch {
    // localStorage negato (Safari private, cookie di terze parti bloccati):
    // il gate ricomparira' a ogni visita. Preferibile a un crash all'avvio.
  }
  return "pending";
}

/**
 * Gate 18+ all'ingresso del sito, prima della registrazione.
 *
 * Bloccante per costruzione: niente X, niente Esc, niente click-fuori. Un gate
 * chiudibile senza rispondere non attesta nulla, quindi l'unica via d'uscita e'
 * la conferma esplicita di essere maggiorenne.
 *
 * Mostrato ai soli visitatori anonimi: chi ha un account ha gia' dichiarato la
 * data di nascita in registrazione e il server l'ha validata fail-closed
 * (auth.ts), quindi ripetere la domanda a ogni sessione sarebbe attrito senza
 * guadagno. Per la stessa ragione l'onboarding non la richiede una terza volta.
 *
 * La schermata di rifiuto non e' piu' raggiungibile dalla UI, ma resta per chi
 * si era dichiarato minorenne quando il bottone esisteva: persiste al reload,
 * cosi' non c'e' un "ci ho ripensato" a un click di distanza.
 *
 * Nota: come ogni age gate lato client e' aggirabile (basta svuotare il
 * localStorage). Vale come attestazione, non come verifica tecnica: quella
 * resta la data di nascita raccolta in registrazione.
 */
export function AgeGate() {
  const { isAuthenticated, isLoading } = useAuth();
  const [decision, setDecision] = useState<Decision>(readDecision);

  const visible = !isLoading && !isAuthenticated && decision !== "adult";

  // Scroll di fondo bloccato finche' il gate e' aperto: senza, la pagina scorre
  // dietro al backdrop e il contenuto resta parzialmente leggibile.
  useEffect(() => {
    if (!visible) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  const persist = (value: "1" | "0") => {
    try {
      localStorage.setItem(LS_KEY, value);
    } catch {
      // Storage non disponibile: la decisione vale per questa sessione.
    }
  };

  if (!visible) return null;

  const blocked = decision === "minor";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Verifica dell'età"
      // z sopra a tutto il resto (il banner marketing sta a 10050): questo gate
      // deve coprire anche gli altri overlay, non finirci sotto.
      // `dark` sul contenitore, non sul documento: il gate e' scuro anche
      // quando il sito e' in light mode (come da mockup), e cosi' i token
      // --primary/--card risolvono ai valori dark reali del tema invece di
      // essere riscritti a mano con degli hex.
      className="dark fixed inset-0 z-[10100] flex items-center justify-center p-4"
      // I crawler che eseguono JS (Googlebot) sono visitatori anonimi e vedono
      // il gate aperto: senza questo usano il suo testo come snippet nei
      // risultati invece della description/landing.
      data-nosnippet=""
    >
      {/* Backdrop opaco e sfocato: il sito dietro non deve essere leggibile.
          Nessun onClick — cliccare fuori non chiude un age gate. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
      />

      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-primary/45 bg-card text-card-foreground shadow-[0_0_60px_-12px_hsl(var(--primary)/0.45)] animate-in fade-in zoom-in-95 duration-200">
        {blocked ? (
          <div className="px-6 py-10 text-center sm:px-10">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-destructive/40 bg-destructive/10">
              <Ban className="h-7 w-7 text-destructive" />
            </div>
            <h2 className="mt-5 text-xl font-bold leading-tight">
              Accesso riservato ai maggiorenni
            </h2>
            <p className="mx-auto mt-2.5 max-w-xs text-sm leading-relaxed text-muted-foreground">
              Questo sito è riservato a chi ha compiuto 18 anni. Non puoi
              accedere.
            </p>
          </div>
        ) : (
          <div className="px-6 py-9 sm:px-10 sm:py-10">
            <div className="flex flex-col items-center text-center">
              <img
                src="/assets/logo.png"
                alt="CercAmanti"
                className="h-12 w-auto object-contain sm:h-14"
              />

              <h2 className="mt-5 text-2xl font-bold leading-tight sm:text-[28px]">
                Benvenuto su CercAmanti
              </h2>

              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                CercAmanti è una piattaforma dedicata a contenuti e
                conversazioni per un pubblico adulto.
                <br className="hidden sm:block" /> Per accedere, devi avere 18
                anni o più.
              </p>
            </div>

            {/* Bottone brand pieno con alone: e' l'unica azione della schermata,
                quindi porta tutto il peso visivo. */}
            <button
              type="button"
              className="mt-7 flex h-14 w-full items-center justify-center gap-3 rounded-full bg-primary text-base font-bold uppercase tracking-wide text-primary-foreground shadow-[0_0_35px_-6px_hsl(var(--primary)/0.85)] transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              onClick={() => {
                persist("1");
                setDecision("adult");
              }}
              data-testid="button-age-gate-adult"
            >
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary-foreground/70 text-[11px] font-semibold"
              >
                18+
              </span>
              Ho 18 anni o più
            </button>

            {/* Tre rassicurazioni in riga, separate da filetti verticali come
                nel mockup; su mobile restano in riga ma piu' strette. */}
            <div className="mt-7 grid grid-cols-3 divide-x divide-border">
              {[
                { Icon: ShieldCheck, label: "Ambiente sicuro\ne moderato" },
                { Icon: Lock, label: "I tuoi dati sono\nprotetti" },
                { Icon: EyeOff, label: "Spazio discreto\ne riservato" },
              ].map(({ Icon, label }) => (
                <div
                  key={label}
                  className="flex flex-col items-center gap-2 px-1 text-center"
                >
                  <Icon className="h-6 w-6 text-primary" />
                  <span className="whitespace-pre-line text-[11px] leading-snug text-muted-foreground">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-7 border-t border-border pt-5 text-center">
              {/* Informativa AI Act (art. 50): il gate precede la registrazione,
                  quindi arriva prima di qualsiasi interazione con le Amanti. */}
              <p
                className="mb-3 text-[11px] leading-snug text-muted-foreground"
                data-testid="text-age-gate-ai-disclosure"
              >
                Tutti i profili presenti su questo sito, e le relative
                interazioni, sono generati e gestiti mediante sistemi di
                intelligenza artificiale e non corrispondono a persone reali.
              </p>
              <p className="flex items-start justify-center gap-2 text-[12px] leading-snug text-muted-foreground">
                <Ban
                  aria-hidden="true"
                  className="mt-px h-4 w-4 shrink-0 text-primary"
                />
                <span>
                  Se non hai ancora 18 anni, ti preghiamo di lasciare questo
                  sito.
                </span>
              </p>
              <a
                href="/termini"
                className="mt-3 inline-block text-[12px] text-primary underline underline-offset-2 hover:text-primary/80"
              >
                Termini di servizio
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
